<template>
  <Flex direction="column">
    <router-view />
    <LastMinuteDiscount />
    <DiscoverDynamicPricing />
    <BaseCard :heading="$t('marketing.discounts.title')">
      <template #header>
        <Flex>
          <BaseSearch
            v-model="variables.search"
            debounce
            v-test="'discounts-search'"
          />
          <BaseButton
            :routerLink="{ name: 'create-discount-code' }"
            v-test="'createDiscountCodeButton'"
          >
            {{ $t('marketing.discounts.new_discount_code') }}
          </BaseButton>
        </Flex>
      </template>
      <BaseTable
        v-if="discountCodes.length"
        :headers="[
          $t('global.code'),
          $t('global.type'),
          $t('global.used'),
          $t('global.status')
        ]"
        :rows="
          discountCodes.map((c) => ({
            id: c.id,
            actions: ['edit', 'delete'],
            routerLink: {
              name: 'discount-details',
              params: { discountId: c.id }
            },
            cells: [
              c.code,
              $t(`marketing.discounts.category.${c.category}`),
              `${c.usedCount} ${$t('global.items.customer', c.usedCount === 1 ? 1 : 2)}`,
              {
                label: c.state.toLowerCase()
              }
            ]
          }))
        "
        @action="onOptionClick"
        v-test="'discounts-table'"
      />
    </BaseCard>
    <EmptyPageContent
      v-show="!loading && !discountCodes.length"
      imageName="discounts"
      :text="
        showEmptyPageContent
          ? {
              title: $t('marketing.discounts.title'),
              description: $t('marketing.discounts.empty-list.description'),
              btnPrimary: filters.capitalize(
                $t('marketing.discounts.new_discount_code')
              )
            }
          : null
      "
      @click="onEmptyAction"
      v-test="'discountsOnboarding'"
    />
    <LoadMore
      v-show="!allDataFetched"
      @shown="fetchMore"
    />
  </Flex>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { useUserStore } from '@/stores/user';
import DiscoverDynamicPricing from './DiscoverDynamicPricing.vue';
import LastMinuteDiscount from './LastMinuteDiscount.vue';

export default defineComponent({
  components: {
    LastMinuteDiscount,
    EmptyPageContent,
    LoadMore,
    DiscoverDynamicPricing,
  },
  setup() {
    const { discountCodes, loading, variables, allDataFetched, fetchMore } =
      usePagination({
        fieldName: 'discountCodes',
        query: gql`
          query getDiscountCodes(
            $search: String
            $pagination: PaginationAttributes
          ) {
            discountCodes(search: $search, pagination: $pagination) {
              id
              code
              category
              usedCount
              state
            }
          }
        `,
        variables: {
          search: '',
        },
      });
    return {
      discountCodes,
      loading,
      variables,
      fetchMore,
      allDataFetched,
      filters,
    };
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    showEmptyPageContent() {
      return !this.variables.search && !this.discountCodes.length;
    },
  },
  methods: {
    onEmptyAction() {
      this.$router.push({ name: 'create-discount-code' });
    },
    onOptionClick(item) {
      if (item.option === 'edit') {
        this.$router.push({
          name: 'edit-discount-code',
          params: { discountId: item.id },
        });
      }

      if (item.option === 'delete') {
        this.$router.push({
          name: 'delete-discount-code',
          params: { discountId: item.id },
        });
      }
    },
  },
});
</script>
